import { z } from 'zod'

import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export const appTagFormSchema = z.object({
  name: z.string().trim().min(1),
})

export type AppTagFormValues = z.infer<typeof appTagFormSchema>

export interface AdminAppTag {
  app_count: number
  created_by?: string | null
  created_by_name?: string | null
  created_at: string | null
  id: string
  name: string
  normalized_name: string
  updated_by?: string | null
  updated_by_name?: string | null
  updated_at: string | null
}

export interface AdminAppTagsListData {
  items: AdminAppTag[]
  page: number
  page_size: number
  total: number
}

export interface AdminAppTagsListParams {
  order?: 'asc' | 'desc'
  orderBy?: string
  page: number
  pageSize: number
  search?: string
}

export interface TagBoundApp {
  id: string
  name: string
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message?: string
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function appendOptionalQuery(params: URLSearchParams, key: string, value: string | undefined) {
  const normalized = value?.trim()

  if (normalized) {
    params.set(key, normalized)
  }
}

function tagPayload(values: AppTagFormValues) {
  const parsed = appTagFormSchema.parse(values)

  return {
    name: parsed.name,
  }
}

export async function listAdminAppTags({
  order = 'asc',
  orderBy = 'name',
  page,
  pageSize,
  search,
}: AdminAppTagsListParams) {
  const params = new URLSearchParams({
    order,
    order_by: orderBy,
    page: String(page),
    page_size: String(pageSize),
  })

  appendOptionalQuery(params, 'search', search)

  const response = await apiClient.request<SuccessEnvelope<AdminAppTagsListData>>(
    `/admin/app-tags?${params.toString()}`,
  )

  return response.data
}

export async function createAdminAppTag(values: AppTagFormValues) {
  const response = await apiClient.request<SuccessEnvelope<AdminAppTag>>('/admin/app-tags', {
    body: JSON.stringify(tagPayload(values)),
    method: 'POST',
  })

  return response.data
}

export async function updateAdminAppTag(tagId: string, values: AppTagFormValues) {
  const response = await apiClient.request<SuccessEnvelope<AdminAppTag>>(
    `/admin/app-tags/${encodeURIComponent(tagId)}`,
    {
      body: JSON.stringify(tagPayload(values)),
      method: 'PUT',
    },
  )

  return response.data
}

export async function deleteAdminAppTag(tagId: string) {
  return apiClient.request<MessageEnvelope>(`/admin/app-tags/${encodeURIComponent(tagId)}`, {
    method: 'DELETE',
  })
}

export async function listAdminAppTagApps(tagId: string) {
  const response = await apiClient.request<SuccessEnvelope<{ items: TagBoundApp[] }>>(
    `/admin/app-tags/${encodeURIComponent(tagId)}/apps`,
  )

  return response.data.items
}
